import React from "react"
import { useTranslation } from "react-i18next"

import { connect } from "scrivito"
import { ScrivitoImage } from "scrivito/scrivito-image/ScrivitoImage"
import { ensureString } from "shared/lib/ensureString"
import { BuildingType } from "shared/types/references/BuildingType"
import { MediaOption } from "shared/ui/media-option/MediaOption"
import { TextHighlighter } from "shared/ui/text-highlighter/TextHighlighter"

import { useBuildingReferenceInfoText } from "entities/building-references/lib/useBuildingReferenceInfoText"

import { BuildingReferenceOption } from "../../../../types/GlobalSearch"
import { GlobalSearchListItem } from "./GlobalSearchListItem"

interface BuildingReferenceGlobalSearchListItemProps {
  option: BuildingReferenceOption
  index: number
  isTabActive: boolean
}

const BuildingReferenceGlobalSearchListItemContent = connect(
  ({ option }: Pick<BuildingReferenceGlobalSearchListItemProps, "option">) => {
    const { t } = useTranslation()
    const { obj, matchInfo } = option.data
    const imageWidget = obj.get("imageWidgets")?.[0] ?? null
    const subtext = useBuildingReferenceInfoText(obj)

    const buildingTypes = obj.get("buildingType") as BuildingType[] | undefined
    const city = obj.get("contact")?.get("city")?.toString() ?? ""

    return (
      <MediaOption.Root variant="product">
        <MediaOption.Media aria-hidden>
          <ScrivitoImage
            content={imageWidget}
            attribute="image"
            alt={ensureString(imageWidget?.get("alternativeText"))}
          />
        </MediaOption.Media>
        <MediaOption.Label>
          <TextHighlighter
            text={option.label}
            searchTerm={option.data.matchInfo.title?.searchTerm}
            ranges={option.data.matchInfo.title?.ranges}
          />
        </MediaOption.Label>
        {subtext && (
          <MediaOption.Subtext>
            {buildingTypes && buildingTypes.length > 0 ? (
              <span className="global-search__item-subtext-part">
                {buildingTypes?.map((buildingType, index) => (
                  <React.Fragment key={buildingType}>
                    <TextHighlighter
                      text={t(`BuildingReference.BuildingType.${buildingType}`)}
                      searchTerm={
                        matchInfo.buildingTypes?.[buildingType]?.searchTerm
                      }
                      ranges={matchInfo.buildingTypes?.[buildingType]?.ranges}
                    />
                    {index < buildingTypes.length - 1 ? (
                      <span>{", "}</span>
                    ) : null}
                  </React.Fragment>
                ))}
              </span>
            ) : null}
            <span className="global-search__item-subtext-part">{city}</span>

            {matchInfo.products?.map(({ value, ranges }) =>
              value ? (
                <span className="global-search__item-subtext-part">
                  <TextHighlighter text={value} ranges={ranges} />
                </span>
              ) : null
            )}
          </MediaOption.Subtext>
        )}
      </MediaOption.Root>
    )
  }
)

export const BuildingReferenceGlobalSearchListItem = React.memo(
  ({
    option,
    index,
    isTabActive
  }: BuildingReferenceGlobalSearchListItemProps) => {
    return (
      <GlobalSearchListItem
        className="global-search__list-item--building-reference"
        index={index}
        option={option}
        isTabActive={isTabActive}
      >
        <BuildingReferenceGlobalSearchListItemContent option={option} />
      </GlobalSearchListItem>
    )
  }
)
